import { pb } from '../api/pocketbase';

export type LogAction = 'ADD' | 'EDIT' | 'VIEW' | 'REMOVE' | 'URL' | 'CLICK' | 'LOGOUT' | 'NONE';

interface QueuedLog {
    userId: string;
    name: string;
    action: LogAction;
    data: Record<string, unknown>;
}

const BATCH_SIZE = 5;
const FLUSH_DELAY_MS = 5_000;

let queue: QueuedLog[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Public IP can't be read in-browser; fetched once per session and cached.
// ponytail: client-side IP is spoofable — move to a PocketBase hook (e.realIP()) when server access exists.
let cachedIp: string | null = null;
fetch('https://api.ipify.org?format=json')
    .then((r) => r.json())
    .then((d) => {
        cachedIp = d.ip;
    })
    .catch(() => {});

// Canvas + WebGL fingerprint, computed once and cached (stable per device/browser).
let cachedFingerprint: { canvas: string | null; webglVendor: string | null; webglRenderer: string | null } | null = null;

const hashString = (s: string): string => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
};

const buildFingerprint = (): typeof cachedFingerprint => {
    let canvas: string | null = null;
    let webglVendor: string | null = null;
    let webglRenderer: string | null = null;

    try {
        const c = document.createElement('canvas');
        const ctx = c.getContext('2d');
        if (ctx) {
            ctx.textBaseline = 'top';
            ctx.font = '14px Arial';
            ctx.fillStyle = '#f60';
            ctx.fillRect(10, 10, 100, 30);
            ctx.fillStyle = '#069';
            ctx.fillText('fingerprint-🔒', 2, 2);
            canvas = hashString(c.toDataURL());
        }
    } catch {}

    try {
        const gl = document.createElement('canvas').getContext('webgl') as WebGLRenderingContext | null;
        const ext = gl?.getExtension('WEBGL_debug_renderer_info');
        if (gl && ext) {
            webglVendor = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) as string;
            webglRenderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
        }
    } catch {}

    return { canvas, webglVendor, webglRenderer };
};

const collectClientInfo = (): Record<string, unknown> => {
    if (!cachedFingerprint) cachedFingerprint = buildFingerprint();

    const nav = navigator as Navigator & {
        deviceMemory?: number;
        userAgentData?: { platform?: string; mobile?: boolean; brands?: unknown };
        connection?: { effectiveType?: string; downlink?: number; rtt?: number };
    };

    return {
        ip: cachedIp,
        ts: new Date().toISOString(),
        url: location.href,
        referrer: document.referrer || null,
        userAgent: nav.userAgent,
        uaData: nav.userAgentData
            ? { platform: nav.userAgentData.platform, mobile: nav.userAgentData.mobile, brands: nav.userAgentData.brands }
            : null,
        platform: nav.platform,
        languages: nav.languages,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        screen: { w: screen.width, h: screen.height, availW: screen.availWidth, availH: screen.availHeight, colorDepth: screen.colorDepth, pixelRatio: window.devicePixelRatio },
        viewport: { w: window.innerWidth, h: window.innerHeight },
        hardwareConcurrency: nav.hardwareConcurrency ?? null,
        deviceMemory: nav.deviceMemory ?? null,
        maxTouchPoints: nav.maxTouchPoints ?? null,
        connection: nav.connection ? { effectiveType: nav.connection.effectiveType, downlink: nav.connection.downlink, rtt: nav.connection.rtt } : null,
        cookieEnabled: nav.cookieEnabled,
        doNotTrack: nav.doNotTrack,
        fingerprint: cachedFingerprint,
    };
};

const flush = (): void => {
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
    if (!queue.length) return;

    const batch = queue.splice(0, queue.length);
    const pbBatch = pb.createBatch();
    for (const entry of batch) {
        pbBatch.collection('usersLogs').create(entry);
    }
    pbBatch.send().catch(() => {});
};

window.addEventListener('beforeunload', flush);

/**
 * Records a user action to usersLogs.
 * Actions are buffered and sent in batches of up to 5,
 * or after 5 seconds of inactivity.
 *
 * @param name   Action description (e.g. "Opened lead John Doe")
 * @param action Action type
 */
const logAction = (name: string, action: LogAction = 'NONE', extra?: Record<string, unknown>): void => {
    const userId = pb.authStore.model?.id;
    if (!userId) return;

    queue.push({ userId, name, action, data: { ...collectClientInfo(), ...extra } });

    if (queue.length >= BATCH_SIZE) {
        flush();
        return;
    }

    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
};

export { logAction };
